import {
  Calendar,
  Download,
  Eye,
  Grid,
  ImagesIcon,
  List,
  Package,
  Palette,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import {
  deleteUserCatalog,
  fetchUserCatalogs,
  selectUserCatalogs,
  selectCatalogFetchError,
  selectUserCatalogsLoaded,
} from "store/slices/catalogSlice";
import {
  deleteUserMockup,
  fetchUserMockups,
  selectUserMockups,
  selectMockupFetchError,
  selectUserMockupsLoaded,
} from "store/slices/mockupSlice";
import ToolsStats from "./ToolsStats";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import LucideIcon from "utils/LucideIcon";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import ConfirmDialog from "shared/Dialog/ConfirmDialog ";
import { extractErrorMessage } from "utils/ErrorHandlers";
import toast from "react-hot-toast";
import Truncate from "utils/Truncate";

const renderPagination = (
  page: number,
  setPage: (p: number) => void,
  totalItems: number,
  itemsPerPage: number
) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  if (totalPages <= 1) return null;

  return (
    <div
      className={`flex items-center justify-center mt-6 gap-6 ${totalItems <= itemsPerPage ? "hidden" : ""
        }`}
    >
      <ButtonSecondary
        disabled={page === 1}
        onClick={() => setPage(page - 1)}
        size="md"
        variant="info"
      >
        Previous
      </ButtonSecondary>
      <span className="px-3 py-1 text-gray-600 font-inter">
        Page {page} of {totalPages}
      </span>
      <ButtonSecondary
        disabled={page === totalPages}
        onClick={() => setPage(page + 1)}
        variant="info"
        size="md"
      >
        Next
      </ButtonSecondary>
    </div>
  );
};

const ToolsDashboard = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("catalogs");
  const [mockupsViewMode, setMockupsViewMode] = useState("grid");
  const [catalogsViewMode, setCatalogsViewMode] = useState("grid");
  const userCatalogs = useAppSelector(selectUserCatalogs);
  const userMockups = useAppSelector(selectUserMockups);
  const catalogError = useAppSelector(selectCatalogFetchError);
  const mockupError = useAppSelector(selectMockupFetchError);
  const isCatalogLoaded = useAppSelector(selectUserCatalogsLoaded);
  const isMockupLoaded = useAppSelector(selectUserMockupsLoaded);
  const [mockupsLoading, setMockupsLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [searchCatalogTerm, setSearchCatalogTerm] = useState("");
  const [searchMockupTerm, setSearchMockupTerm] = useState("");

  const [showDialog, setShowDialog] = useState(false);
  const [toolId, setToolId] = useState(""); // Fixed typo: tollgId -> toolId
  const [selectedToolType, setSelectedToolType] = useState("");
  const [deletingTool, setDeletingTool] = useState(false);

  const toolConfigs = [
    { id: "catalogs", name: "Catalogs", icon: Package },
    { id: "mockups", name: "Mockups", icon: Palette },
  ];

  const handleFetchToolsData = async (fetchData: string) => {
    if (fetchData === "catalog") {
      setCatalogLoading(true);
      try {
        await dispatch(fetchUserCatalogs()).unwrap();
        setCatalogLoading(false);
      } catch (err) {
        console.error("Failed to fetch catalogs", err);
        setCatalogLoading(false);
      }
    } else {
      setMockupsLoading(true);
      try {
        await dispatch(fetchUserMockups()).unwrap();
      } catch (err) {
        console.error("Failed to fetch mockups", err);
      } finally {
        setMockupsLoading(false);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDownloadCatalog = async (catalog: any) => {
    try {
      const response = await fetch(catalog.catalog);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${catalog.name || "catalog"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading catalog:", error);
      toast.error("Failed to download catalog");
    }
  };

  const handleDeleteCatalog = async (catalogId: string) => {
    setDeletingTool(true);
    try {
      await dispatch(deleteUserCatalog(catalogId)).unwrap();
      setDeletingTool(false);
      setShowDialog(false); // Close dialog after deletion
    } catch (error) {
      const message = extractErrorMessage(error);
      setDeletingTool(false);
      return message;
    }
  };

  const handleDeleteMockup = async (mockupId: string) => {
    setDeletingTool(true);
    try {
      await dispatch(deleteUserMockup(mockupId)).unwrap();
      setDeletingTool(false);
      setShowDialog(false); // Close dialog after deletion
    } catch (error) {
      const message = extractErrorMessage(error);
      setDeletingTool(false);
      return message;
    }
  };

  const handleDownloadMockup = async (mockup: any) => {
    try {
      const response = await fetch(mockup.mockup);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${mockup.name || "mockup"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading mockup:", error);
      toast.error("Failed to download mockup");
    }
  };

  // Debounce hook
  function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const timeout = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => clearTimeout(timeout);
    }, [value, delay]);
    return debouncedValue;
  }

  const [catalogPage, setCatalogPage] = useState(1);
  const [mockupPage, setMockupPage] = useState(1);

  const itemsPerRow =
    catalogsViewMode === "grid" || mockupsViewMode === "grid" ? 2 : 2;
  const rowsPerPage =
    catalogsViewMode === "grid" || mockupsViewMode === "grid" ? 5 : 2;
  const itemsPerPage = itemsPerRow * rowsPerPage;

  const debouncedCatalogSearch = useDebounce(searchCatalogTerm, 200);
  const debouncedMockupSearch = useDebounce(searchMockupTerm, 200);

  // Enhanced filtering
  const filteredCatalogs = Array.isArray(userCatalogs)
    ? userCatalogs.filter((catalog: any) =>
      [catalog.name, catalog.description]
        .filter(Boolean)
        .some((field) =>
          field.toLowerCase().includes(debouncedCatalogSearch.toLowerCase())
        )
    )
    : [];
  const filteredMockups = Array.isArray(userMockups)
    ? userMockups.filter((mockup: any) =>
      [mockup.name, mockup.product, mockup.description]
        .filter(Boolean)
        .some((field) =>
          field.toLowerCase().includes(debouncedMockupSearch.toLowerCase())
        )
    )
    : [];

  // Pagination
  const paginatedCatalogs = filteredCatalogs.slice(
    (catalogPage - 1) * itemsPerPage,
    catalogPage * itemsPerPage
  );
  const paginatedMockups = filteredMockups.slice(
    (mockupPage - 1) * itemsPerPage,
    mockupPage * itemsPerPage
  );

  // Reset page when search or view mode changes
  useEffect(() => {
    setCatalogPage(1);
  }, [catalogsViewMode, debouncedCatalogSearch]);

  useEffect(() => {
    setMockupPage(1);
  }, [mockupsViewMode, debouncedMockupSearch]);

  const viewMockup = (index: number) => {
    const mockup = userMockups[index];
    if (!mockup?.mockup) return;

    window.open(mockup.mockup, "_blank");
  };

  return (
    <>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className=" bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-2xl border border-white/20 p-2 md:p-8 relative overflow-hidden">
          <div className="py-3 sm:py-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="md:flex items-center gap-3 text-center md:text-left">
                <div>
                  <h1 className="text-2xl font-bold font-inter text-secondary-gradient">
                    Tools Usage
                  </h1>
                  <p className="text-gray-600 text-base mt-1 font-inter">
                    Manage all your projects and creations in one place
                  </p>
                </div>
              </div>
            </div>
            <ToolsStats />
          </div>
          {/* Tab Navigation */}
          <div className="bg-white rounded-2xl shadow-sm">
            <div className="border-b border-gray-200 overflow-x-auto">
              <nav className="flex gap-8 px-6">
                {toolConfigs.map((tool) => {
                  const IconComponent = tool.icon;
                  return (
                    <button
                      key={tool.id}
                      onClick={() => setActiveTab(tool.id)}
                      className={`flex items-center gap-2 pt-4 pb-3 px-2 border-b-2 font-medium text-sm transition-colors mb-1 ${activeTab === tool.id
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span className="font-inter">{tool.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === "catalogs" && (
                <div>
                  {/* Filters and Search */}
                  <div className="pb-4 md:p-6 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-1">
                        <div className="relative min-w-0 flex-1 max-w-md">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <input
                            type="text"
                            placeholder="Search catalog name..."
                            value={searchCatalogTerm}
                            onChange={(e) =>
                              setSearchCatalogTerm(e.target.value)
                            }
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCatalogsViewMode("grid")}
                          className={`p-2 rounded-lg ${catalogsViewMode === "grid"
                            ? "bg-blue-100 text-blue-600"
                            : "text-gray-500 hover:bg-gray-100"
                            }`}
                        >
                          <Grid className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setCatalogsViewMode("list")}
                          className={`p-2 rounded-lg ${catalogsViewMode === "list"
                            ? "bg-blue-100 text-blue-600"
                            : "text-gray-500 hover:bg-gray-100"
                            }`}
                        >
                          <List className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="py-4">
                    {catalogError ? (
                      <ErrorFallback
                        message={catalogError}
                        className="min-h-fit"
                        onRetry={() => handleFetchToolsData("catalog")}
                      />
                    ) : catalogLoading || !isCatalogLoaded ? (
                      <div className="flex justify-center items-center">
                        <img
                          src={infiniteLoader}
                          alt="Loading..."
                          className="w-[12%]"
                        />
                      </div>
                    ) : Array.isArray(userCatalogs) &&
                      userCatalogs.length === 0 ? (
                      <div className="text-center py-12">
                        <ImagesIcon
                          size={48}
                          className="text-gray-400 mx-auto mb-4"
                        />
                        <p className="text-gray-500 text-lg font-inter">
                          No catalogs created yet
                        </p>
                        <p className="text-gray-400 text-sm font-inter">
                          Start by creating your first catalog
                        </p>
                      </div>
                    ) : (
                      <div
                        className={
                          catalogsViewMode === "grid"
                            ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                            : "space-y-4"
                        }
                      >
                        {paginatedCatalogs.length > 0 ? (
                          paginatedCatalogs.map((catalog: any) => (
                            <div
                              key={catalog.id}
                              className={`bg-white border border-gray-200 rounded-xl hover:shadow-lg transition-all duration-200 group ${catalogsViewMode === "list"
                                ? "flex items-center p-4"
                                : ""
                                }`}
                            >
                              <div
                                className={
                                  catalogsViewMode === "grid" ? "p-6" : "flex-1"
                                }
                              >
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                      <h4 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">
                                        <Truncate
                                          text={catalog.name}
                                          maxLength={40}
                                        />
                                      </h4>
                                    </div>
                                    <div
                                      className={`space-y-4 ${catalogsViewMode === "list" &&
                                        "flex gap-4 items-center space-y-0"
                                        }`}
                                    >
                                      <p className="text-gray-600">
                                        # {String(catalog.id).slice(-4)}
                                      </p>
                                      <div className="flex items-center text-sm text-gray-500">
                                        <span className="flex items-center gap-1">
                                          <Calendar className="h-4 w-4 mr-1" />
                                          {formatDate(catalog.createdAt)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center justify-end gap-3">
                                  <button
                                    onClick={() =>
                                      handleDownloadCatalog(catalog)
                                    }
                                    className="p-2 text-blue-500 hover:text-blue-700 rounded-lg hover:bg-blue-100"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowDialog(true);
                                      setToolId(catalog.id);
                                      setSelectedToolType("catalog");
                                    }}
                                    className="p-2 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-12 md:col-span-2 xl:col-span-3">
                            <LucideIcon
                              size={48}
                              name="Search"
                              className="text-gray-400 mx-auto mb-4"
                            />
                            <p className="text-gray-500 text-lg font-inter">
                              No catalogs matched your search.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {Array.isArray(filteredCatalogs) &&
                    renderPagination(
                      catalogPage,
                      setCatalogPage,
                      filteredCatalogs.length,
                      itemsPerPage
                    )}
                </div>
              )}

              {activeTab === "mockups" && (
                <div>
                  {/* Filters and Search */}
                  <div className="pb-4 md:p-6 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-1">
                        <div className="relative min-w-0 flex-1 max-w-md">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <input
                            type="text"
                            placeholder="Search mockup name..."
                            value={searchMockupTerm}
                            onChange={(e) =>
                              setSearchMockupTerm(e.target.value)
                            }
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setMockupsViewMode("grid")}
                          className={`p-2 rounded-lg ${mockupsViewMode === "grid"
                            ? "bg-blue-100 text-blue-600"
                            : "text-gray-500 hover:bg-gray-100"
                            }`}
                        >
                          <Grid className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setMockupsViewMode("list")}
                          className={`p-2 rounded-lg ${mockupsViewMode === "list"
                            ? "bg-blue-100 text-blue-600"
                            : "text-gray-500 hover:bg-gray-100"
                            }`}
                        >
                          <List className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="py-4">
                    {mockupError ? (
                      <ErrorFallback
                        message={mockupError}
                        className="min-h-fit"
                        onRetry={() => handleFetchToolsData("mockup")}
                      />
                    ) : mockupsLoading || !isMockupLoaded ? (
                      <div className="flex justify-center items-center">
                        <img
                          src={infiniteLoader}
                          alt="Loading..."
                          className="w-[12%]"
                        />
                      </div>
                    ) : Array.isArray(userMockups) &&
                      userMockups.length === 0 ? (
                      <div className="text-center py-12">
                        <ImagesIcon
                          size={48}
                          className="text-gray-400 mx-auto mb-4"
                        />
                        <p className="text-gray-500 text-lg font-inter">
                          No mockups created yet
                        </p>
                        <p className="text-gray-400 text-sm font-inter">
                          Start by creating your first mockup
                        </p>
                      </div>
                    ) : (
                      <div
                        className={
                          mockupsViewMode === "grid"
                            ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                            : "space-y-4"
                        }
                      >
                        {paginatedMockups.length > 0 ? (
                          paginatedMockups.map((mockup: any, index) => (
                            <div
                              key={mockup.id}
                              className={`bg-white border border-gray-200 rounded-xl hover:shadow-lg transition-all duration-200 group ${mockupsViewMode === "list"
                                ? "flex items-center p-4"
                                : ""
                                }`}
                            >
                              <div
                                className={
                                  mockupsViewMode === "grid" ? "p-6" : "flex-1"
                                }
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center space-x-3">
                                    <h4 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                                      <Truncate
                                        text={mockup.name}
                                        maxLength={50}
                                      />
                                    </h4>
                                  </div>
                                </div>
                                <div
                                  className={`space-y-4 ${mockupsViewMode === "list" &&
                                    "flex gap-4 items-center space-y-0"
                                    }`}
                                >
                                  <p className="text-gray-600">
                                    # {String(mockup.id).slice(-4)}
                                  </p>
                                  <div className="flex items-center text-sm text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-4 w-4 mr-1" />
                                      {formatDate(mockup.createdAt)}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-end gap-4">
                                  <button
                                    onClick={() => viewMockup(index)}
                                    className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDownloadMockup(mockup)}
                                    className="p-2 text-blue-500 hover:text-blue-700 rounded-lg hover:bg-blue-100"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowDialog(true);
                                      setToolId(mockup.id);
                                      setSelectedToolType("mockup");
                                    }}
                                    className="p-2 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-12 md:col-span-2 xl:col-span-3">
                            <LucideIcon
                              size={48}
                              name="Search"
                              className="text-gray-400 mx-auto mb-4"
                            />
                            <p className="text-gray-500 text-lg font-inter">
                              No mockups matched your search.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {Array.isArray(filteredMockups) &&
                    renderPagination(
                      mockupPage,
                      setMockupPage,
                      filteredMockups.length,
                      itemsPerPage
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        loading={deletingTool}
        onConfirm={() =>
          selectedToolType === "catalog"
            ? handleDeleteCatalog(toolId)
            : handleDeleteMockup(toolId)
        }
        title={
          selectedToolType === "catalog" ? "Delete Catalog" : "Delete Mockup"
        }
        message={`Are you sure you want to delete the ${selectedToolType === "catalog" ? "catalog" : "mockup"
          }? This action cannot be undone.`}
      />
    </>
  );
};

export default ToolsDashboard;
